import {
  OutPrescriptionStatus,
  type PrescriptionDoctorApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

// A prescription is "Pending authorization in Treat" only when both signals agree:
// the backend returned a Treat encounter link AND the status is one of the
// pending-authorization variants. EncounterUrl alone is ambiguous — it can come
// back populated for already-ordered scripts as a "view in Treat" deep link.
const PENDING_AUTHORIZATION_STATUSES = new Set(['pending', 'pendingauthorization'])

export const isPendingAuthorization = (p: Pick<PrescriptionDoctorApiOutContract, 'EncounterUrl' | 'Status'>): boolean =>
  !!p.EncounterUrl && PENDING_AUTHORIZATION_STATUSES.has((p.Status ?? '').toLowerCase())

// Cancelled, or never reached the pharmacy. A cancel still in flight, denied or failed leaves the
// script live. Lowercased like `isPendingAuthorization`: the backend sends casings the generated enum lacks.
const NO_LONGER_LIVE_STATUSES = new Set<string>([
  OutPrescriptionStatus.cancel_success,
  OutPrescriptionStatus.cancelled,
  OutPrescriptionStatus.xmit_failed,
])

export const isNoLongerLive = (p: Pick<PrescriptionDoctorApiOutContract, 'Status'>): boolean =>
  NO_LONGER_LIVE_STATUSES.has((p.Status ?? '').toLowerCase())

// "Sent" = at least one prescription that is NOT merely pending authorization in Treat.
//
// Platform-agnostic: scripts written through any prescribing surface — Treat, the DAW/ScriptSure
// encounter flow ("Add"), or the in-app DTS product order — all land in the same
// getVisitsVisitIdPrescriptions collection, so this check covers every path, not just Treat.
//
// Why this gates the inactivity warning: the backend's claim-timeout auto-release is gated on
// whether the visit has a Treat/ScriptSure *encounter*, not on prescriptions directly. But the
// backend creates that encounter while writing the prescription (before it transmits), and the
// encounter row persists regardless of the script's final status. So any visit carrying such a
// prescription always has an encounter and is never auto-released — suppressing here is a
// conservative subset of the backend's hold and never hides a warning the backend would act on.
// This is also why cancelled/failed scripts correctly count as "sent": the encounter still exists.
//
// Known gap (intentional): an encounter with no transmitted script — a pending-authorization or
// review-only visit — also holds the claim backend-side but is not caught here, so the warning can
// still fire for it. That is the benign direction (it nudges, it doesn't drop a visit). Mirroring
// the backend exactly needs a has-encounter / claim-held flag the provider API does not expose yet.
export const hasSentPrescription = (
  prescriptions: readonly PrescriptionDoctorApiOutContract[] | null | undefined,
): boolean => !!prescriptions?.some((p) => !isPendingAuthorization(p))
